import store, { type StoreBase } from "store2";

type StorageAreaInternal = StoreBase & {
  _area?: Storage;
  _in?: (key: string) => string;
};

export type StorageFacade = {
  get: <T>(key: string, fallback: T) => T;
  set: <T>(key: string, value: T) => T;
  setString: (key: string, value: string) => string;
  remove: <T>(key: string, fallback?: T) => T | undefined;
  has: (key: string) => boolean;
  clear: () => void;
  namespace: (name: string) => StorageFacade;
  getString: (key: string, fallback?: string) => string;
  getBoolean: (key: string, fallback?: boolean) => boolean;
  getNumber: (key: string, fallback?: number) => number;
};

export type WMUStoreAPI = {
  local: StorageFacade;
  session: StorageFacade;
};

function normalizeBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "1" || normalized === "true" || normalized === "on" || normalized === "yes") return true;
  if (normalized === "0" || normalized === "false" || normalized === "off" || normalized === "no") return false;
  return fallback;
}

function storageArea(area: StoreBase): Storage | null {
  const internal = area as StorageAreaInternal;
  return internal._area instanceof Storage ? internal._area : null;
}

function storageKey(area: StoreBase, key: string): string {
  const internal = area as StorageAreaInternal;
  return typeof internal._in === "function" ? internal._in(key) : key;
}

function readRaw(area: StoreBase, key: string): string | null {
  try {
    return storageArea(area)?.getItem(storageKey(area, key)) ?? null;
  } catch (_err) {
    return null;
  }
}

function writeRaw(area: StoreBase, key: string, value: string): void {
  try {
    storageArea(area)?.setItem(storageKey(area, key), value);
  } catch (_err) {
    // ignore quota/security failures
  }
}

function removeRaw(area: StoreBase, key: string): void {
  try {
    storageArea(area)?.removeItem(storageKey(area, key));
  } catch (_err) {
    // ignore cleanup failures
  }
}

function parseRawValue(raw: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch (_err) {
    return { ok: false };
  }
}

function looksLikeBrokenStructuredValue(raw: string): boolean {
  const trimmed = raw.trim();
  return (
    trimmed.startsWith("{") ||
    trimmed.startsWith("[") ||
    trimmed === "undefined" ||
    trimmed === "NaN"
  );
}

function createStorageFacade(area: StoreBase): StorageFacade {
  const facade: StorageFacade = {
    get<T>(key: string, fallback: T): T {
      const raw = readRaw(area, key);
      if (raw === null) return fallback;
      const parsed = parseRawValue(raw);
      if (parsed.ok) {
        return parsed.value as T;
      }
      if (looksLikeBrokenStructuredValue(raw) || typeof fallback === "object") {
        removeRaw(area, key);
        return fallback;
      }
      try {
        const value = area.get(key);
        return (value === undefined ? fallback : value) as T;
      } catch (_err) {
        removeRaw(area, key);
        return fallback;
      }
    },
    set<T>(key: string, value: T): T {
      try {
        area.set(key, value);
      } catch (_err) {
        // ignore quota/security failures
      }
      return value;
    },
    setString(key: string, value: string): string {
      writeRaw(area, key, String(value));
      return String(value);
    },
    remove<T>(key: string, fallback?: T): T | undefined {
      try {
        return area.remove(key, fallback) as T | undefined;
      } catch (_err) {
        return fallback;
      }
    },
    has(key: string): boolean {
      try {
        return area.has(key);
      } catch (_err) {
        return false;
      }
    },
    clear(): void {
      try {
        area.clear();
      } catch (_err) {
        // ignore
      }
    },
    namespace(name: string): StorageFacade {
      return createStorageFacade(area.namespace(name, true));
    },
    getString(key: string, fallback = ""): string {
      const raw = readRaw(area, key);
      if (raw === null) return fallback;
      const parsed = parseRawValue(raw);
      if (!parsed.ok) return raw;
      if (parsed.value === null || parsed.value === undefined) return fallback;
      return String(parsed.value);
    },
    getBoolean(key: string, fallback = false): boolean {
      return normalizeBoolean(facade.get(key, fallback), fallback);
    },
    getNumber(key: string, fallback = 0): number {
      const value = Number(facade.get<string | number | null | undefined>(key, fallback));
      return Number.isFinite(value) ? value : fallback;
    },
  };
  return facade;
}

export const localStore = createStorageFacade(store.local);
export const sessionStore = createStorageFacade(store.session);

window.WMUStore = {
  local: localStore,
  session: sessionStore,
} satisfies WMUStoreAPI;
